import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, CheckCircle2, ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';

const ForgotPassword = () => {
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await forgotPassword(email.trim().toLowerCase());
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'No pudimos procesar la solicitud');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16">
      <div className="bg-white rounded-3xl border border-[#E7E5E4] overflow-hidden">
        {sent ? (
          <div className="p-10 text-center" data-testid="forgot-sent">
            <div className="w-14 h-14 rounded-full bg-[#F7F2E8] mx-auto flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8 text-[#2A5C43]" />
            </div>
            <h1 className="font-display font-bold text-2xl text-[#1C1917] mb-2">Correo enviado</h1>
            <p className="text-[#57534E] text-sm mb-6">
              Si <b>{email}</b> está registrado en nuestra base, recibirás un enlace para restablecer tu contraseña en los próximos minutos. Revisa también tu carpeta de spam.
            </p>
            <Button asChild variant="outline" className="rounded-full border-[#2A5C43] text-[#2A5C43]">
              <Link to="/login" data-testid="back-to-login">
                <ArrowLeft className="w-4 h-4 mr-2" /> Volver a iniciar sesión
              </Link>
            </Button>
          </div>
        ) : (
          <>
            <div className="bg-[#2A5C43] text-white px-8 py-8">
              <p className="text-xs tracking-[0.25em] uppercase text-[#F7F2E8] font-semibold mb-1">Recuperación</p>
              <h1 className="font-display font-bold text-2xl">¿Olvidaste tu contraseña?</h1>
              <p className="text-sm text-white/85 mt-2">Ingresa tu correo y te enviaremos un enlace para restablecerla.</p>
            </div>

            <form onSubmit={submit} className="p-8 space-y-4">
              <div>
                <Label htmlFor="email" className="text-xs uppercase tracking-widest text-[#8BA58F] font-semibold">Correo</Label>
                <div className="relative mt-1">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8BA58F]" />
                  <Input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@correo.com"
                    className="pl-10"
                    data-testid="forgot-email"
                  />
                </div>
              </div>

              {error && (
                <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="forgot-error">{error}</p>
              )}

              <Button
                type="submit"
                disabled={loading || !email}
                className="w-full bg-[#2A5C43] hover:bg-[#214A35] text-white rounded-full py-5 font-semibold"
                data-testid="forgot-submit"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Enviar enlace
              </Button>

              <div className="text-center pt-2">
                <Link to="/login" className="text-xs text-[#2A5C43] hover:underline">← Volver</Link>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
